import { Controller } from "@hotwired/stimulus"

// Manages appearance preferences (theme + UI zoom) with persistence via AJAX.
//
// Usage (on <html> tag):
//   <html data-controller="theme"
//         data-theme-current-value="light"
//         data-theme-zoom-value="100"
//         data-theme-url-value="/en/users/1/update_settings">
//
// Theme toggles (Light / Dark / System):
//   data-action="theme#setLight" | "theme#setDark" | "theme#setSystem"
//
// Zoom presets (90 / 100 / 110 / 125):
//   data-action="click->theme#setZoom"
//   data-zoom="90" data-zoom-option="90"
export default class extends Controller {
  static values = {
    current: { type: String, default: "light" },
    zoom: { type: String, default: "100" },
    url: String
  }

  connect() {
    this._mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this._onSystemChange = this._onSystemChange.bind(this)
    this._mediaQuery.addEventListener("change", this._onSystemChange)
    this._apply()
    this._applyZoom()
  }

  disconnect() {
    this._mediaQuery.removeEventListener("change", this._onSystemChange)
  }

  setLight() { this._setTheme("light") }
  setDark() { this._setTheme("dark") }
  setSystem() { this._setTheme("system") }

  setZoom(event) {
    const zoom = event.currentTarget.dataset.zoom
    if (!zoom || zoom === this.zoomValue) return
    this.zoomValue = zoom
    this._applyZoom()
    this._persist({ ui_zoom: zoom })
    this._updateZoomUI()
  }

  _setTheme(theme) {
    this.currentValue = theme
    this._apply()
    this._persist({ theme })
    this._updateToggleUI()
  }

  _apply() {
    const resolved = this._resolvedTheme()
    document.documentElement.setAttribute("data-theme", resolved)
    this._syncGanttTheme(resolved)
  }

  _applyZoom() {
    const pct = parseInt(this.zoomValue, 10) || 100
    const factor = (pct / 100).toString()
    // --ui-zoom is consumed by layout CSS (e.g. sidebar h-screen compensation)
    // so viewport-pinned elements stay flush under CSS `zoom`.
    document.documentElement.style.setProperty("--ui-zoom", factor)
    if (pct === 100) {
      document.documentElement.style.removeProperty("zoom")
    } else {
      document.documentElement.style.zoom = factor
    }
  }

  _resolvedTheme() {
    if (this.currentValue === "system") {
      return this._mediaQuery.matches ? "dark" : "light"
    }
    return this.currentValue
  }

  _onSystemChange() {
    if (this.currentValue === "system") {
      this._apply()
    }
  }

  _updateToggleUI() {
    document.querySelectorAll("[data-theme-option]").forEach(btn => {
      const isActive = btn.dataset.themeOption === this.currentValue
      btn.classList.toggle("bg-brand", isActive)
      btn.classList.toggle("text-white", isActive)
      btn.classList.toggle("text-text-muted", !isActive)
    })
  }

  _updateZoomUI() {
    document.querySelectorAll("[data-zoom-option]").forEach(btn => {
      const isActive = btn.dataset.zoomOption === this.zoomValue
      btn.classList.toggle("bg-brand", isActive)
      btn.classList.toggle("text-white", isActive)
      btn.classList.toggle("text-text-muted", !isActive)
    })
  }

  _syncGanttTheme(resolved) {
    const link = document.getElementById("gantt-theme")
    if (!link) return
    const expected = resolved === "dark" ? "/vendor/bryntum/gantt.material-dark.css" : "/vendor/bryntum/gantt.material.css"
    if (!link.href.endsWith(expected)) link.href = expected
  }

  _persist(payload) {
    if (!this.hasUrlValue) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.urlValue, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        "Accept": "application/json"
      },
      body: JSON.stringify({ setting: payload })
    }).catch(() => {
      // Silently fail — appearance preferences are cosmetic
    })
  }
}
